/**
 * @category Types
 * @typedef {Object} PriceType
 * @property {string} ONCE_OFF - Once off.
 * @property {string} MONTHLY - Monthly.
 * @property {string} PAY_IN_STORE - Pay in store.
 * @property {string} FREE - Free.
 */
export enum PriceType {
  ONCE_OFF = 'ONCE_OFF',
  MONTHLY = 'MONTHLY',
  PAY_IN_STORE = 'PAY_IN_STORE',
  FREE = 'FREE',
}

/**
 * @typedef {Object} AcquiringChannel
 * @property {string} buyOnlineFicaOnline - Bought Online – FICA Online.
 * @property {string} buyInstoreFicaOnline - Bought Instore – FICA Online.
 * @property {string} rentOnlineFicaOnline - Rented Online – FICA Online.
 * @property {string} zeroRentOnlineFicaOnline - Zero Rental Online – FICA Online.
 * @property {string} buyInstoreFicaInstore - Bought Instore – FICA Instore.
 * @property {string} vodapay - VodaPay BizWallet.
 * @property {string} vodacomBusiness - Vodacom Business.
 * @property {string} thirdPartyChannel - 3rd Party.
 * @property {string} noAcquiringChannelSet - No Acquiring Channel set.
 */
export enum AcquiringChannel {
  buyOnlineFicaOnline = 'Bought Online – FICA Online',
  buyInstoreFicaOnline = 'Bought Instore – FICA Online',
  rentOnlineFicaOnline = 'Rented Online – FICA Online',
  zeroRentOnlineFicaOnline = 'Zero Rental Online – FICA Online',
  buyInstoreFicaInstore = 'Bought Instore – FICA Instore',
  vodapay = 'VodaPay BizWallet',
  vodacomBusiness = 'Vodacom Business',
  thirdPartyChannel = '3rd Party',
  noAcquiringChannelSet = 'No Acquiring Channel set',
}

/**
 * @typedef {Object} PaymentMethodCode
 * @property {string} CASH - Cash.
 * @property {string} ONLINE_PAYMENT - Online Payment.
 * @property {string} PAYMENT_LINK - Payment Link.
 * @property {string} PAYFLEX - Payflex.
 * @property {string} PAYFLEX_LINK - Payflex Link.
 * @property {string} RENTAL - Rental.
 * @property {string} ZERO - Zero.
 */
export enum PaymentMethodCode {
  CASH = 'Cash',
  ONLINE_PAYMENT = 'Online Payment',
  PAYMENT_LINK = 'Payment Link',
  PAYFLEX = 'Payflex',
  PAYFLEX_LINK = 'Payflex Link',
  RENTAL = 'Rental',
  ZERO = 'Zero',
}

/**
 * @typedef {Object} DeliveryOptions
 * @property {string} Deliver - Deliver.
 * @property {string} Stock - Stock on hand.
 * @property {string} None - None.
 */
export enum DeliveryOptions {
  Deliver = 'Deliver',
  Stock = 'Stock on hand',
  None = ''
}

/**
 * @typedef {Object} CartItem
 * @property {string} productId - The ID of the product.
 * @property {AcquiringChannel} acquiringChannel - The channel through which the product was acquired.
 * @property {string} title - The title of the product.
 * @property {number} quantity - The quantity of the product in the cart.
 * @property {string} tagline - The tagline of the product.
 * @property {number} [price] - The price of the product.
 * @property {PriceType} [priceType] - The type of the price.
 * @property {string[]} [serialNumbers] - The serial numbers of the product.
 * @property {string[]} [friendlyTerminalName] - The friendly terminal names.
 * @property {string} [deliveryOption] - The delivery option for the product.
 * @property {boolean} isTenderType - Whether the product is a tender type.
 * @property {DeliveryOptions[]} deliveryOptions - The delivery options for the product.
 * @property {PaymentMethodCode|null} [paymentMethodCode] - The payment method code.
 * @property {string} image - The image of the product.
 * @property {string} [id] - The ID of the tender type.
 * @property {string} [productCategoryName] - The name of the product category.
 * @property {string} [otpAddressId] - The ID of the address for continue with OTP.
 */
export type CartItem = {
  productId: string;
  acquiringChannel: AcquiringChannel;
  title: string;
  quantity: number;
  tagline: string;
  price?: number;
  priceType?: PriceType;
  serialNumbers?: string[];
  friendlyTerminalName?: string[];
  deliveryOption?: string;
  isTenderType: boolean;
  deliveryOptions: DeliveryOptions[];
  paymentMethodCode?: PaymentMethodCode | null;
  image: string;
  id: string;
  productCategoryName?: string;
  // used in continue with OTP
  otpAddressId?: string;
};

/**
 * @typedef {Object} AppDelivery
 * @property {string} id - The ID of the delivery.
 * @property {string} productId - The ID of the product.
 * @property {string} addressId - The ID of the address.
 * @property {number} quantity - The quantity of the product in the delivery.
 * @property {DeliveryOptions} [deliveryOption] - The delivery option.
 */
export type AppDelivery = {
  id: string;
  productId: string;
  addressId: string;
  quantity: number;
  deliveryOption?: DeliveryOptions;
};

/**
 * @typedef {Object} Collection
 * @property {string} id - The ID of the collection.
 * @property {string} productId - The ID of the product.
 * @property {string} addressId - The ID of the address.
 * @property {number} quantity - The quantity of the product in the delivery.
 * @property {DeliveryOptions} [deliveryOption] - The delivery option.
 */
export type Collection = {
  id: string;
  productId: string;
  addressId?: string;
  quantity: number;
  cartItemId: string;
  deliveryOption?: DeliveryOptions;
};

/**
 * @typedef {Object} DataStatus
 * @property {string} IDLE - Idle.
 * @property {string} LOADING - Loading.
 * @property {string} LOADED - Loaded.
 */
export enum DataStatus {
  IDLE = 'IDLE',
  LOADING = 'LOADING',
  LOADED = 'LOADED',
}

/**
 * @typedef {Object} Device
 * @property {string} title - The title of the device.
 * @property {string[]} [featureTextList] - The list of feature texts.
 * @property {string} [imageBase64] - The base64 image of the device.
 * @property {string} [imageURL] - The URL of the image.
 * @property {string} id - The ID of the device.
 * @property {AcquiringChannel} acquiringChannel - The acquiring channel.
 * @property {AcquiringChannel} [secondaryAcquiringChannel] - The secondary acquiring channel.
 * @property {boolean} isProductCategory - Whether the device is a product category.
 * @property {string} productCategoryName - The name of the product category.
 * @property {boolean} isCardPresent - Whether the card is present.
 * @property {PaymentMethodCode[]} allowedPaymentMethods - The allowed payment methods.
 * @property {string} tagline - The tagline of the device.
 * @property {string[]} extraBenefits - The extra benefits.
 * @property {string[]} [featureImageURLList] - The list of feature image URLs.
 * @property {number} [minQuantity] - The minimum quantity.
 * @property {number} [maxQuantity] - The maximum quantity.
 * @property {number} quantity - The quantity.
 * @property {number} quantityOld - The old quantity.
 * @property {DeliveryOptions} [deliveryOption] - The delivery option.
 * @property {DeliveryOptions[]} deliveryOptions - The delivery options.
 * @property {string[]} [serialNumbers] - The serial numbers.
 * @property {string[]} [friendlyTerminalName] - The friendly terminal names.
 * @property {boolean} [isDeliverable] - Whether the device is deliverable.
 * @property {boolean} [isVirtual] - Whether the device is virtual.
 * @property {boolean} isTenderType - Whether the device is a tender type.
 * @property {PaymentMethodCode} [paymentMethodCode] - The payment method code.
 */
export type Device = {
  // below values from API response
  title: string;
  featureTextList?: string[];
  imageBase64?: string;
  imageURL?: string;
  id: string;
  acquiringChannel: AcquiringChannel;
  secondaryAcquiringChannel?: AcquiringChannel;
  isProductCategory: boolean;
  productCategoryName: string;
  isCardPresent: boolean;
  allowedPaymentMethods: PaymentMethodCode[];
  tagline: string;
  extraBenefits: string[];

  // below values derived from API response
  featureImageURLList?: string[];
  minQuantity?: number;
  maxQuantity?: number;

  // below values set by frontend
  quantity: number;
  quantityOld: number;
  deliveryOption?: DeliveryOptions;
  deliveryOptions: DeliveryOptions[];
  serialNumbers?: string[];
  friendlyTerminalName?: string[];
  isDeliverable?: boolean;
  isVirtual?: boolean;
  isTenderType: boolean;
  paymentMethodCode?: PaymentMethodCode;
};

/**
 * @typedef {Object} Fee
 * @property {string} name - The name of the fee.
 * @property {number} percentage - The percentage of the fee.
 */
export type Fee = {
  name: string;
  percentage: number;
};

/**
 * @typedef {Object} CatalogueItem
 * @property {number} index - The index of the catalogue item.
 * @property {Device} device - The device.
 * @property {Object} card - The card.
 * @property {DataStatus} card.status - The status of the card.
 * @property {Fee[]} [card.fees] - The fees of the card.
 * @property {Object} price - The price.
 * @property {DataStatus} price.status - The status of the price.
 * @property {number} [price.price] - The price.
 * @property {PriceType} [price.priceType] - The type of the price.
 * @property {number} [price.price] - The price.
 * @property {PriceType} [price.priceType] - The type of the price.
 */
export type CatalogueItem = {
  index: number;
  device: Device;
  card: {
    status: DataStatus;
    fees?: Fee[];
  };
  price: {
    status: DataStatus;
    /* in cents, VAT exclusive */
    price?: number;
    priceType?: PriceType;
  };
};